import CopyToClipboard from 'components/CopyToClipboard'
import I18n from 'components/I18n'
import Link from 'components/Link'
import { OverlayLoader } from 'components/Loaders'
import Text from 'components/Text'
import { walletCurrencies } from 'constants/currency'
import { actions, selectors } from 'data'
import { StateType } from 'data/types'
import messages from 'lang'
import { QRCodeCanvas } from 'qrcode.react'
import React, { useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { ModalCard, ModalTitle } from 'scenes/Cashflow/styles'
import DataError from 'scenes/DataError'

import CurrencyDepositMessage from '../CurrencyDepositMessage'
import { Expanded, Input, InputWrapper, MemoContainer, MemoLink, Spacer } from '../styles'
import {
  Divider,
  MessageInline,
  StyledAddressWrapper,
  StyledCeruleanButton,
  StyledCryptoWrapper,
  StyledDivider,
  StyledHeader,
  SytledColumn
} from './styles'

const memoLink = 'https://support.blockchain.com/hc/en-us/articles/360018797312-Stellar-memos'
const xrpTagLink = 'https://exchange-support.blockchain.com/hc/en-us/articles/360043533292'

type QrProps = { value: string }
export const Qr = ({ value }: QrProps) => <QRCodeCanvas level='M' size={142} value={value} />

export const getHref = (currency, address, locale, account, walletUrl) => {
  const baseUrl = `${walletUrl}/${locale}`
  if (currency === 'BTC') {
    return `${baseUrl}/#/open/bitcoin:${address || ''}`
  }
  if (currency === 'XLM') {
    return `${baseUrl}/#/open/xlm?address=${address || ''}&memo=${account?.memo || ''}`
  }
  return baseUrl
}

type Props = {
  currency: string
}
const DepositCryptoAddress = ({ currency }: Props) => {
  const intl = useIntl()
  const addressInputRef = useRef<HTMLInputElement>(null)
  const accounts = useSelector(selectors.payments.getAccounts)
  const { walletUrl } = useSelector(selectors.config.getConfig)
  const currencyData = useSelector((state: StateType) =>
    selectors.currencies.getCurrency(state, currency)
  )
  const data = accounts?.[currency]
  const account = data?.value
  const error = data?.error
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(actions.payments.fetchAccount(currency))
  }, [currency])

  const isWalletCurrency = walletCurrencies.some((currencyItem) => currencyItem === currency)

  const trackCopiedToClipboard = () => {
    dispatch(
      actions.analytics.trackEvent({
        key: 'Exchange Deposit Address Copied-Shared',
        properties: {
          currency,
          network: 'ERC20',
          type: 'COPIED'
        }
      })
    )
  }

  if (error) {
    return (
      <ModalCard>
        <StyledHeader>
          <ModalTitle>
            <I18n data={messages.deposit} />
          </ModalTitle>
        </StyledHeader>
        <DataError error={error} />
      </ModalCard>
    )
  }

  if (!account) return <OverlayLoader />

  return (
    <ModalCard>
      <StyledHeader>
        <ModalTitle>
          <I18n data={messages.deposit_currency} currency={currency} />
        </ModalTitle>
      </StyledHeader>
      <Divider />
      <StyledCryptoWrapper>
        <InputWrapper>
          <Input
            e2e='depositCryptoAddress'
            label={intl.formatMessage(messages.label_wallet_address, {
              value: currencyData?.displaySymbol ?? currency
            })}
            name='account.address'
            onFocus={() => {
              if (addressInputRef.current !== null) {
                addressInputRef.current.select()
              }
            }}
            readOnly
            ref={addressInputRef}
            value={account?.address}
            variant='cashflow'
          />
          <CopyToClipboard light value={account?.address} onCopied={trackCopiedToClipboard} />
        </InputWrapper>
        {currency === 'XLM' && (
          <MemoContainer>
            <MemoLink href={memoLink} rel='noopener noreferrer' target='_blank'>
              <Text color='blue600' size='xs' weight={500}>
                <I18n data={messages.deposit_what_is_memo} />
              </Text>
            </MemoLink>
            <Spacer />
            <InputWrapper>
              <Input
                e2e='depositCryptoMemo'
                label={intl.formatMessage(messages.label_memo, {
                  value: currency
                })}
                name='account.memo'
                readOnly
                value={account?.memo}
                variant='cashflow'
              />
              <CopyToClipboard value={account?.memo ?? ''} />
            </InputWrapper>
          </MemoContainer>
        )}
        {currency === 'XRP' && (
          <MemoContainer>
            <MemoLink href={xrpTagLink} rel='noopener noreferrer' target='_blank'>
              <Text color='blue600' size='xs' weight={500}>
                <I18n data={messages.deposit_what_is_tag} />
              </Text>
            </MemoLink>
            <Spacer />
            <InputWrapper>
              <Input
                e2e='depositCryptoTag'
                label={intl.formatMessage(messages.label_memo, {
                  value: currency
                })}
                name='account.tag'
                readOnly
                value={account?.tag}
                variant='cashflow'
              />
              <CopyToClipboard value={account?.tag ?? ''} />
            </InputWrapper>
          </MemoContainer>
        )}
        <StyledAddressWrapper>
          <Expanded>
            <Qr value={account?.address} />
          </Expanded>
          <CurrencyDepositMessage currency={currency} />
        </StyledAddressWrapper>
      </StyledCryptoWrapper>
      {isWalletCurrency && (
        <>
          <StyledDivider>
            <MessageInline>
              <I18n data={messages.or} />
            </MessageInline>
          </StyledDivider>
          <SytledColumn>
            <Link
              href={getHref(currency, account?.address, intl.locale, account, walletUrl)}
              onClick={() => {
                dispatch(
                  actions.analytics.trackEvent({
                    key: 'Exchange Deposit From Wallet Clicked',
                    properties: { currency }
                  })
                )
              }}
              rel='noopener noreferrer'
              target='_blank'
            >
              <StyledCeruleanButton e2e='depositButton'>
                <Text weight={600}>
                  <I18n data={messages.button_deposit_blockchain_wallet} />
                </Text>
              </StyledCeruleanButton>
            </Link>
          </SytledColumn>
        </>
      )}
    </ModalCard>
  )
}

export default DepositCryptoAddress
